import { brokerPlanIdForTier, defaultSeatTierId } from "@billing/catalog.js";
import { recordLoginEvent } from "@hub";
import { finalizeNewAccount } from "./authOnboarding.js";
import { syncSessionOnboarding } from "./onboardingState.js";
import { ensureJourneyDb, getUserByEmail, insertRow, listTable, updateRow } from "../db";
import { checkCredential, hasCredential, setCredential } from "../db/credentials";
import { createUser } from "../db/schema";
import { assertProfessionalMayLogin } from "../api/accountLifecycle";
import {
  accountsLogin,
  accountsRegister,
  clearAccountsToken,
  isAccountsApiEnabled,
  setAccountsToken,
} from "../api/accountsApi";

const SESSION_KEY = "dh_session";

export const DEV_ADMIN = {
  id: "admin-dev",
  buyerId: "buyer-admin",
  name: "Dev Admin",
  email: "admin@dreamhome.local",
  password: "demo123",
};

export const SITE_ADMIN = {
  id: "admin-site",
  name: "Site Admin",
  email: "siteadmin@rechecklist.pro",
  tempPassword: "RC-Site-2026",
};

export function isAdminRole(role) {
  return role === "admin";
}

export function hasBuyerAccess(role) {
  return role === "buyer" || role === "admin";
}

export function hasBrokerAccess(role) {
  return role === "broker" || role === "corporate" || role === "admin";
}

export function hasClientAccess(role) {
  return role === "buyer" || role === "seller" || role === "admin";
}

export function hasAgentAccess(role) {
  return role === "agent" || role === "admin";
}

export function getSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
}

export function setSession(session) {
  const next = syncSessionOnboarding(session);
  localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  if (session?.userId) {
    try {
      recordLoginEvent({
        user_id: session.userId,
        email: session.email || "",
        name: session.name || "",
        role: session.role || "",
      });
    } catch {
      /* hub store is optional on first paint */
    }
  }
  return next;
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  clearAccountsToken();
}

/**
 * Prefer MariaDB Accounts API when configured.
 * AUTH failures reject; network/unreachable falls through to local demo auth.
 */
async function tryRemoteLogin(email, password, expectedRole) {
  if (!isAccountsApiEnabled()) return null;
  try {
    const data = await accountsLogin({ email, password });
    const role = data.session?.role;
    if (expectedRole && role !== expectedRole && role !== "admin") {
      throw new Error(`This account is registered as ${role}, not ${expectedRole}`);
    }
    if (tokenOrSessionNeedsLifecycle(role, data.session?.userId)) {
      try {
        assertProfessionalMayLogin({
          id: data.session.userId,
          role_type: role,
          email: data.session.email,
          status: "active",
        });
      } catch (lifecycleErr) {
        throw lifecycleErr;
      }
    }
    setAccountsToken(data.token);
    return setSession({
      role: data.session.role,
      userId: data.session.userId,
      name: data.session.name,
      email: data.session.email,
      brokerageId: data.session.brokerageId || null,
      assignedAgentId: data.session.assignedAgentId || null,
      privileges: data.session.privileges || (data.session.role === "admin" ? "full" : null),
      token: data.token,
      must_rotate: Boolean(data.must_rotate),
      expires_at: data.expires_at || null,
      authSource: "accounts-api",
    });
  } catch (err) {
    if (err?.code === "NETWORK" || err?.code === "AUTH") return null;
    throw err;
  }
}

function tokenOrSessionNeedsLifecycle(role) {
  return role === "agent" || role === "broker";
}

async function tryRemoteRegister(role, data) {
  if (!isAccountsApiEnabled()) return null;
  const name =
    data.name ||
    `${data.first_name || data.firstName || ""} ${data.last_name || data.lastName || ""}`.trim() ||
    data.email;
  try {
    const payload = {
      role_type: role,
      email: data.email,
      password: data.password || "demo123",
      name,
      first_name: data.first_name || data.firstName || "",
      last_name: data.last_name || data.lastName || "",
      phone: data.phone || "",
      office_name: data.office || data.office_name || "",
      license_number: data.license || data.license_number || "",
      client_code: data.client_code || "",
      assigned_agent_id: data.agentId || data.assigned_agent_id || null,
    };
    await accountsRegister(payload);
    const session = await tryRemoteLogin(data.email, payload.password, role);
    if (!session) throw new Error("Registered, but sign-in failed. Try signing in.");
    return { session, remote: true };
  } catch (err) {
    if (err?.code === "NETWORK") return null;
    throw err;
  }
}

export async function loginBuyer({ email, password }) {
  const remote = await tryRemoteLogin(email, password, "buyer");
  if (remote) return remote;
  const adminSession = sessionFromAdminLogin(email, password);
  if (adminSession) return adminSession;
  const buyers = JSON.parse(localStorage.getItem("dh_buyers") || "[]");
  const buyer = buyers.find(
    (item) =>
      item.email.toLowerCase() === String(email || "").toLowerCase() &&
      item.password === password
  );
  if (buyer) {
    const session = {
      role: "buyer",
      userId: buyer.id,
      name: buyer.name,
      email: buyer.email,
    };
    setSession(session);
    return session;
  }
  ensureJourneyDb();
  const user = getUserByEmail(email);
  if (user && user.role_type === "buyer" && user.status !== "inactive" && checkCredential(user.id, password)) {
    const session = {
      role: "buyer",
      userId: user.id,
      name: user.name,
      email: user.email,
    };
    setSession(session);
    return session;
  }
  throw new Error("Invalid email or password");
}

export async function registerBuyer(data) {
  const remote = await tryRemoteRegister("buyer", data);
  ensureJourneyDb();
  if (!remote) assertEmailFree(data.email);
  const buyers = JSON.parse(localStorage.getItem("dh_buyers") || "[]");
  const linkedAgent = findUserByNameOrEmail(["agent"], data.agentName || data.agent);
  const linkedBroker = findUserByNameOrEmail(["broker"], data.brokerName || data.broker);
  const buyerId = remote?.session?.userId || crypto.randomUUID();
  const existingIdx = buyers.findIndex(
    (item) => String(item.email || "").toLowerCase() === String(data.email || "").toLowerCase()
  );
  const buyer = {
    id: buyerId,
    name: data.name,
    email: data.email,
    phone: data.phone || "",
    password: remote ? "" : data.password || "demo123",
    agentId: linkedAgent?.id || data.agentId || "",
    agentName: data.agentName || data.agent || linkedAgent?.name || "",
    brokerName: data.brokerName || data.broker || linkedBroker?.name || "",
    office: data.office || data.office_name || "",
    preferences: {
      location: {},
      homeType: {},
      lot: {},
      exterior: {},
      interior: {},
      lifestyle: {},
      accessibility: {},
      notes: {
        dealBreakers: "",
        mustHaves: "",
        householdNeeds: "",
        priorities: "",
        specialNotes: "",
        attachments: [],
      },
      tipsAcknowledged: false,
      transactionChecklist: {},
    },
    status: "active",
    createdAt: new Date().toISOString(),
  };
  if (existingIdx === -1) buyers.push(buyer);
  else buyers[existingIdx] = { ...buyers[existingIdx], ...buyer };
  localStorage.setItem("dh_buyers", JSON.stringify(buyers));
  let session =
    remote?.session ||
    setSession({
      role: "buyer",
      userId: buyer.id,
      name: buyer.name,
      email: buyer.email,
    });
  ensureClientJourneyUser({
    id: buyer.id,
    role_type: "buyer",
    name: buyer.name,
    email: buyer.email,
    office_name: buyer.office,
    linked_agent_name: buyer.agentName,
    linked_broker_name: buyer.brokerName,
    brokerage_id: linkedBroker?.id || null,
  });
  session = finalizeNewAccount(session, "buyer");
  return { buyer, session };
}

function betaFieldsFromInput(data = {}) {
  return {
    first_name: data.first_name || data.firstName || "",
    last_name: data.last_name || data.lastName || "",
    phone: data.phone || "",
    license_number: data.license_number || data.license || "",
    betaTester: Boolean(data.betaTester),
    betaAccepted: Boolean(data.betaAccepted),
    betaRole: data.betaRole || null,
    betaAcceptedAt: data.betaAcceptedAt || null,
  };
}

function sessionFromUser(user, extra = {}) {
  return {
    role: extra.role || user.role_type,
    userId: user.id,
    name: user.name,
    email: user.email,
    office_name: user.office_name || extra.office_name || "",
    plan: user.plan,
    plan_status: user.plan_status,
    seat_tier: user.seat_tier,
    brokerageId: extra.brokerageId ?? (user.role_type === "broker" ? user.id : user.brokerage_id),
    phone: user.phone || extra.phone || "",
    license_number: user.license_number || extra.license_number || "",
    betaTester: Boolean(user.betaTester),
    betaAccepted: Boolean(user.betaAccepted),
    betaRole: user.betaRole || extra.betaRole || null,
    betaAcceptedAt: user.betaAcceptedAt || extra.betaAcceptedAt || null,
  };
}

export async function registerAgent(data) {
  const remote = await tryRemoteRegister("agent", data);
  ensureJourneyDb();
  if (!remote) assertEmailFree(data.email);
  const broker = findUserByNameOrEmail(["broker"], data.broker || data.brokerName);
  const password = data.password || "demo123";
  let user = getUserByEmail(data.email);
  if (!user) {
    user = insertRow(
      "users",
      createUser({
        id: remote?.session?.userId,
        role_type: "agent",
        name: data.name,
        email: data.email,
        status: "active",
        brokerage_id: remote?.session?.brokerageId || broker?.id || null,
        office_name: data.office || data.office_name || "",
        plan: "agent_monthly",
        plan_status: data.skipPaidGate || data.betaTester ? "active" : "signup",
        linked_broker_name: data.broker || data.brokerName || broker?.name || "",
        ...betaFieldsFromInput(data),
      })
    );
  }
  if (!remote) setCredential(user.id, password);
  writeAgentRecord({
    id: user.id,
    name: user.name,
    email: user.email,
    phone: data.phone || "",
    password: remote ? "" : password,
    role: "agent",
    office: user.office_name,
    brokerName: user.linked_broker_name,
    brokerageId: user.brokerage_id,
    plan: user.plan,
    planStatus: user.plan_status,
    licenseNumber: data.license || data.license_number || "",
    betaTester: Boolean(user.betaTester),
    betaAccepted: Boolean(user.betaAccepted),
    betaRole: user.betaRole,
    clients: [],
  });
  let session = remote?.session || setSession(sessionFromUser(user, { role: "agent" }));
  if (!data.betaTester) session = finalizeNewAccount(session, "agent");
  return { user, session };
}

export async function registerBroker(data) {
  const remote = await tryRemoteRegister("broker", data);
  ensureJourneyDb();
  if (!remote) assertEmailFree(data.email);
  const password = data.password || "demo123";
  let user = getUserByEmail(data.email);
  if (!user) {
    user = insertRow(
      "users",
      createUser({
        id: remote?.session?.userId,
        role_type: "broker",
        name: data.name,
        email: data.email,
        status: "active",
        office_name: data.office || data.office_name || "",
        plan: data.plan || brokerPlanIdForTier(data.tier || data.seat_tier || defaultSeatTierId()),
        plan_status: data.skipPaidGate || data.betaTester ? "active" : "signup",
        seat_tier: data.tier || data.seat_tier || defaultSeatTierId(),
        ...betaFieldsFromInput(data),
      })
    );
  }
  if (!remote) setCredential(user.id, password);
  let session =
    remote?.session ||
    setSession(sessionFromUser(user, { role: "broker", brokerageId: user.id }));
  if (!data.betaTester) session = finalizeNewAccount(session, "broker");
  return { user, session };
}

export function registerBetaTester(profile) {
  ensureJourneyDb();
  const role = profile.role === "broker" ? "broker" : "agent";
  const name = `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
  const payload = {
    name,
    firstName: profile.firstName,
    lastName: profile.lastName,
    email: profile.email,
    phone: profile.phone,
    office: profile.brokerage,
    office_name: profile.brokerage,
    license: profile.license,
    license_number: profile.license,
    password: "demo123",
    betaTester: true,
    betaAccepted: true,
    betaRole: role,
    betaAcceptedAt: profile.acceptedAt || new Date().toISOString(),
  };
  const existing = getUserByEmail(payload.email);
  if (existing) {
    const user = updateRow("users", existing.id, {
      name,
      office_name: payload.office_name || existing.office_name,
      role_type: role,
      ...betaFieldsFromInput(payload),
    });
    setCredential(user.id, payload.password);
    if (role === "agent") {
      writeAgentRecord({
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        password: payload.password,
        role: "agent",
        office: user.office_name,
        brokerName: user.linked_broker_name,
        brokerageId: user.brokerage_id,
        plan: user.plan,
        planStatus: user.plan_status,
        licenseNumber: user.license_number,
        betaTester: true,
        betaAccepted: true,
        betaRole: "agent",
        betaAcceptedAt: user.betaAcceptedAt,
        clients: [],
      });
    }
    const session = setSession(
      sessionFromUser(user, {
        role,
        brokerageId: role === "broker" ? user.id : user.brokerage_id,
      })
    );
    return Promise.resolve({ user, session, reused: true });
  }
  if (role === "broker") return registerBroker(payload);
  return registerAgent(payload);
}

export async function registerSeller(data) {
  const remote = await tryRemoteRegister("seller", data);
  ensureJourneyDb();
  if (!remote) assertEmailFree(data.email);
  const linkedAgent = findUserByNameOrEmail(["agent"], data.agentName || data.agent);
  const linkedBroker = findUserByNameOrEmail(["broker"], data.brokerName || data.broker);
  const password = data.password || "demo123";
  let user = getUserByEmail(data.email);
  if (!user) {
    user = insertRow(
      "users",
      createUser({
        id: remote?.session?.userId,
        role_type: "seller",
        name: data.name,
        email: data.email,
        status: "active",
        brokerage_id: linkedBroker?.id || null,
        office_name: data.office || data.office_name || "",
        linked_agent_name: data.agentName || data.agent || linkedAgent?.name || "",
        linked_broker_name: data.brokerName || data.broker || linkedBroker?.name || "",
      })
    );
  }
  if (!remote) setCredential(user.id, password);
  persistSellerAccount({
    id: user.id,
    name: user.name,
    email: user.email,
    phone: data.phone || "",
    agentId: linkedAgent?.id || "",
    agentName: user.linked_agent_name,
    brokerName: user.linked_broker_name,
    office: user.office_name,
    status: "active",
  });
  let session =
    remote?.session ||
    setSession({
      role: "seller",
      userId: user.id,
      name: user.name,
      email: user.email,
    });
  session = finalizeNewAccount(session, "seller");
  return { user, session };
}

export async function loginAgentAccount({ email, password }) {
  const remote = await tryRemoteLogin(email, password, "agent");
  if (remote) return remote;
  const adminSession = sessionFromAdminLogin(email, password);
  if (adminSession) return adminSession;
  const agents = JSON.parse(localStorage.getItem("dh_agents") || "[]");
  const agent = agents.find(
    (item) =>
      item.email.toLowerCase() === String(email || "").toLowerCase() &&
      item.password === password &&
      item.role !== "broker" &&
      item.status !== "deleted" &&
      item.status !== "inactive"
  );
  if (agent) {
    ensureJourneyDb();
    const journey = getUserByEmail(agent.email);
    const merged = {
      ...agent,
      ...journey,
      id: journey?.id || agent.id,
      role_type: "agent",
      office_name: journey?.office_name || agent.office || "",
      plan: journey?.plan || agent.plan,
      plan_status: journey?.plan_status || agent.planStatus,
      betaTester: Boolean(journey?.betaTester || agent.betaTester),
      betaAccepted: Boolean(journey?.betaAccepted || agent.betaAccepted),
      betaRole: journey?.betaRole || agent.betaRole || "agent",
      betaAcceptedAt: journey?.betaAcceptedAt || agent.betaAcceptedAt,
      phone: journey?.phone || agent.phone,
      license_number: journey?.license_number || agent.licenseNumber,
    };
    assertProfessionalMayLogin(merged);
    return setSession(sessionFromUser(merged, { role: "agent" }));
  }
  ensureJourneyDb();
  const journeyAgent = getUserByEmail(email);
  if (
    journeyAgent &&
    journeyAgent.role_type === "agent" &&
    journeyAgent.status !== "inactive" &&
    journeyAgent.status !== "deleted" &&
    checkCredential(journeyAgent.id, password)
  ) {
    assertProfessionalMayLogin(journeyAgent);
    return setSession(sessionFromUser(journeyAgent, { role: "agent" }));
  }
  try {
    return await loginBroker({ email, password });
  } catch {
    throw new Error("Invalid agent credentials");
  }
}

export async function loginBroker({ email, password }) {
  const remote = await tryRemoteLogin(email, password, "broker");
  if (remote) return remote;
  const adminSession = sessionFromAdminLogin(email, password);
  if (adminSession) return adminSession;
  ensureJourneyDb();
  const user = getUserByEmail(email);
  if (!user || user.role_type !== "broker" || user.status === "inactive" || user.status === "deleted") {
    throw new Error("Invalid broker credentials");
  }
  if (!checkCredential(user.id, password)) {
    throw new Error("Invalid broker credentials");
  }
  assertProfessionalMayLogin(user);
  return setSession(sessionFromUser(user, { role: "broker", brokerageId: user.id }));
}

export async function loginSeller({ email, password }) {
  const remote = await tryRemoteLogin(email, password, "seller");
  if (remote) return remote;
  const adminSession = sessionFromAdminLogin(email, password);
  if (adminSession) return adminSession;
  ensureJourneyDb();
  const user = getUserByEmail(email);
  if (!user || user.role_type !== "seller" || user.status === "inactive") {
    throw new Error("Invalid seller credentials");
  }
  if (!checkCredential(user.id, password)) {
    throw new Error("Invalid seller credentials");
  }
  return setSession({
    role: "seller",
    userId: user.id,
    name: user.name,
    email: user.email,
  });
}

export function logout() {
  clearSession();
  return Promise.resolve();
}

function sessionFromAdminLogin(email, password) {
  if (
    String(email || "").toLowerCase() === DEV_ADMIN.email &&
    String(password || "") === DEV_ADMIN.password
  ) {
    return createAdminSession();
  }
  ensureSiteAdmin();
  const user = getUserByEmail(email);
  if (!user || user.role_type !== "admin" || user.status === "inactive" || user.status === "deleted") {
    return null;
  }
  if (!checkCredential(user.id, password)) return null;
  return setSession({
    role: "admin",
    userId: user.id,
    name: user.name,
    email: user.email,
    privileges: "full",
  });
}

function createAdminSession() {
  return setSession({
    role: "admin",
    userId: DEV_ADMIN.id,
    buyerId: DEV_ADMIN.buyerId,
    name: DEV_ADMIN.name,
    email: DEV_ADMIN.email,
    privileges: "full",
  });
}

export function ensureSiteAdmin() {
  ensureJourneyDb();
  let user = getUserByEmail(SITE_ADMIN.email);
  if (!user) {
    user = insertRow(
      "users",
      createUser({
        id: SITE_ADMIN.id,
        role_type: "admin",
        name: SITE_ADMIN.name,
        email: SITE_ADMIN.email,
        status: "active",
        office_name: "REChecklist",
      })
    );
  } else if (user.role_type !== "admin") {
    user = updateRow("users", user.id, { role_type: "admin", status: "active" });
  }
  if (!hasCredential(user.id)) {
    setCredential(user.id, SITE_ADMIN.tempPassword);
  }
  return user;
}

function findUserByNameOrEmail(roleTypes, value) {
  ensureJourneyDb();
  const key = String(value || "").trim().toLowerCase();
  if (!key) return null;
  return (
    listTable("users").find(
      (row) =>
        roleTypes.includes(row.role_type) &&
        (row.email === key || String(row.name || "").toLowerCase() === key)
    ) || null
  );
}

function assertEmailFree(email) {
  const key = String(email || "").trim().toLowerCase();
  if (!key) throw new Error("Email is required");
  ensureJourneyDb();
  if (getUserByEmail(key)) {
    throw new Error("An account with that email already exists");
  }
  const buyers = JSON.parse(localStorage.getItem("dh_buyers") || "[]");
  if (buyers.some((item) => String(item.email || "").toLowerCase() === key)) {
    throw new Error("An account with that email already exists");
  }
  const agents = JSON.parse(localStorage.getItem("dh_agents") || "[]");
  if (agents.some((item) => String(item.email || "").toLowerCase() === key)) {
    throw new Error("An account with that email already exists");
  }
}

function ensureClientJourneyUser(input) {
  ensureJourneyDb();
  if (getUserByEmail(input.email)) return;
  insertRow("users", createUser(input));
}

function writeAgentRecord(record) {
  let agents = [];
  try {
    agents = JSON.parse(localStorage.getItem("dh_agents") || "[]");
  } catch {
    agents = [];
  }
  upsertById(agents, record);
  localStorage.setItem("dh_agents", JSON.stringify(agents));
}

function persistSellerAccount(record) {
  const emptyPortfolio = {
    welcome: {},
    propertyDetails: {},
    interiorFeatures: {},
    exteriorFeatures: {},
    upgrades: {},
    conditionNotes: {},
    photos: {},
    improvements: {},
    listingReadiness: {},
    tips: {},
    portfolioSummary: {},
  };
  const seller = { ...record, portfolio: emptyPortfolio };
  localStorage.setItem("dh_seller", JSON.stringify(seller));
  let list = [];
  try {
    list = JSON.parse(localStorage.getItem("dh_sellers") || "[]");
  } catch {
    list = [];
  }
  const index = list.findIndex((item) => item.id === seller.id);
  if (index === -1) list.push(seller);
  else list[index] = seller;
  localStorage.setItem("dh_sellers", JSON.stringify(list));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("dh-seller-change"));
  }
}

function upsertById(list, record) {
  const index = list.findIndex((item) => item.id === record.id || item.email === record.email);
  if (index === -1) {
    list.push(record);
    return list;
  }
  list[index] = {
    ...record,
    ...list[index],
    id: record.id,
    email: record.email,
    password: record.password,
    name: record.name,
    role: record.role || list[index].role,
  };
  return list;
}

export function ensureDevAdmin() {
  let agents = [];
  let buyers = [];
  try {
    agents = JSON.parse(localStorage.getItem("dh_agents") || "[]");
  } catch {
    agents = [];
  }
  try {
    buyers = JSON.parse(localStorage.getItem("dh_buyers") || "[]");
  } catch {
    buyers = [];
  }

  upsertById(agents, {
    id: DEV_ADMIN.id,
    name: DEV_ADMIN.name,
    email: DEV_ADMIN.email,
    password: DEV_ADMIN.password,
    phone: "(555) 010-0001",
    role: "admin",
    clients: ["buyer-demo", DEV_ADMIN.buyerId],
  });
  localStorage.setItem("dh_agents", JSON.stringify(agents));

  upsertById(buyers, {
    id: DEV_ADMIN.buyerId,
    name: DEV_ADMIN.name,
    email: DEV_ADMIN.email,
    phone: "(555) 010-0001",
    password: DEV_ADMIN.password,
    agentId: DEV_ADMIN.id,
    status: "active",
    preferences: {
      location: {},
      homeType: {},
      lot: {},
      exterior: {},
      interior: {},
      lifestyle: {},
      accessibility: {},
      notes: {
        dealBreakers: "",
        mustHaves: "",
        householdNeeds: "",
        priorities: "",
        specialNotes: "",
        attachments: [],
      },
      tipsAcknowledged: false,
      transactionChecklist: {},
    },
    createdAt: new Date().toISOString(),
  });
  localStorage.setItem("dh_buyers", JSON.stringify(buyers));
  ensureSiteAdmin();
}
